import { openDB, type IDBPDatabase } from 'idb'

/**
 * IndexedDB para dados offline do app.
 *
 * v1: rdos_pendentes
 * v2: + tarefas_pendentes, fvs_pendentes, fvm_pendentes, fotos_pendentes
 *
 * NÃO usa Service Worker. Apenas armazenamento local em IndexedDB.
 */

const DB_NAME = 'construzilla-offline'
const DB_VERSION = 2

export type PendenteStore =
  | 'rdos_pendentes'
  | 'tarefas_pendentes'
  | 'fvs_pendentes'
  | 'fvm_pendentes'
  | 'fotos_pendentes'

const ALL_STORES: PendenteStore[] = [
  'rdos_pendentes',
  'tarefas_pendentes',
  'fvs_pendentes',
  'fvm_pendentes',
  'fotos_pendentes',
]

// ───────────────────────────────────────────────
// RDO (mantido — Etapa 3)
// ───────────────────────────────────────────────
export interface RDOLinkedTaskOffline {
  tarefaId?: string | null
  nome: string
  tipo: 'manual' | 'cronograma'
  percentual: number
  unidade?: string | null
  company_id?: string
}

export interface RDOPendentePayload {
  obra_id: string
  data_registro: string
  clima: string
  frente_servico?: string | null
  ambiente?: string | null
  bloco?: string | null
  andar?: string | null
  unidade?: string | null
  tarefa_id?: string | null
  percentual_exec?: number | null
  servicos_executados?: string | null
  observacoes?: string | null
  paralisacoes?: string | null
  mao_de_obra: any[]
  equipamentos?: any[]
  total_trabalhadores: number
  total_horas_normais: number
  total_horas_extras: number
  company_id: string
  criado_por: string
  /** Tarefas vinculadas — sincronizadas para rdo_tarefas ao ir online */
  linkedTasks?: RDOLinkedTaskOffline[]
}

export interface RDOPendenteRecord extends RDOPendentePayload {
  id: string
  status: 'pendente' | 'sincronizado' | 'erro'
  criado_offline_em: string
  ultima_tentativa_em?: string | null
  ultimo_erro?: string | null
}

// ───────────────────────────────────────────────
// Etapa 4 — payloads dos novos módulos
// ───────────────────────────────────────────────
export interface TarefaPendentePayload {
  // Apenas atualização de status/progresso (criação fica online)
  tipo: 'update_status'
  tarefa_id: string
  obra_id: string
  company_id: string
  criado_por: string
  novo_status: string
  progresso?: number | null
  data_conclusao?: string | null
}

export interface FVSPendentePayload {
  // Cria rascunho FVS (sem fotos/assinatura — política decidida)
  tipo: 'create_rascunho'
  obra_id: string
  company_id: string
  criado_por: string
  titulo: string
  disciplina: string
  servico?: string | null
  pavimento?: string | null
  ambiente?: string | null
  unidade?: string | null
  bloco?: string | null
  responsavel?: string | null
  responsavel_id?: string | null
  data_verificacao: string
  itens: { texto: string; status: string; observacao: string | null; ordem: number }[]
}

export interface FVMPendentePayload {
  // Cria rascunho FVM (sem fotos/assinatura)
  tipo: 'create_rascunho'
  obra_id: string
  company_id: string
  criado_por: string
  titulo: string
  fornecedor: string
  material: string
  lote?: string | null
  nota_fiscal?: string | null
  observacoes?: string | null
  criticidade: string
  status: string
  percentual_conformidade: number
  data_inspecao: string
}

export interface FotoPendentePayload {
  // Foto tirada offline — armazenada como base64 (≤ 2MB pré-compressão)
  obra_id: string
  company_id: string
  criado_por: string
  titulo: string
  categoria: string
  descricao?: string | null
  local_obra?: string | null
  andar?: string | null
  mime_type: string
  tamanho_bytes: number
  base64: string // dataURL completo
  nome_arquivo: string
}

// ───────────────────────────────────────────────
// Genéricos
// ───────────────────────────────────────────────
export interface PendenteRecord<T = any> {
  id: string
  status: 'pendente' | 'erro'
  criado_offline_em: string
  ultima_tentativa_em?: string | null
  ultimo_erro?: string | null
  data: T
}

let dbPromise: Promise<IDBPDatabase> | null = null

export const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // v1
        if (!db.objectStoreNames.contains('rdos_pendentes')) {
          const store = db.createObjectStore('rdos_pendentes', { keyPath: 'id' })
          store.createIndex('obra_id', 'obra_id')
          store.createIndex('status', 'status')
        }
        // v2 — novos stores genéricos (chave: id, indexados por status)
        if (oldVersion < 2) {
          for (const name of ['tarefas_pendentes', 'fvs_pendentes', 'fvm_pendentes', 'fotos_pendentes'] as const) {
            if (!db.objectStoreNames.contains(name)) {
              const s = db.createObjectStore(name, { keyPath: 'id' })
              s.createIndex('status', 'status')
            }
          }
        }
      },
    })
  }
  return dbPromise
}

const newId = (prefix = 'off') =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

// ───────────────────────────────────────────────
// API genérica (Etapa 4)
// ───────────────────────────────────────────────
export const salvarPendente = async <T>(store: PendenteStore, data: T): Promise<PendenteRecord<T>> => {
  if (store === 'rdos_pendentes') {
    throw new Error('Use salvarRDOOffline() para rdos_pendentes (formato legado).')
  }
  const db = await getDB()
  const rec: PendenteRecord<T> = {
    id: newId(store.replace('_pendentes', '')),
    status: 'pendente',
    criado_offline_em: new Date().toISOString(),
    ultima_tentativa_em: null,
    ultimo_erro: null,
    data,
  }
  await db.put(store, rec)
  return rec
}

export const listarPendentes = async <T = any>(store: PendenteStore): Promise<PendenteRecord<T>[]> => {
  const db = await getDB()
  return db.getAllFromIndex(store, 'status', 'pendente') as Promise<PendenteRecord<T>[]>
}

export const contarPendentes = async (store: PendenteStore): Promise<number> => {
  const db = await getDB()
  return db.countFromIndex(store, 'status', 'pendente')
}

export const marcarSincronizado = async (store: PendenteStore, id: string) => {
  const db = await getDB()
  await db.delete(store, id)
}

export const marcarErroPendente = async (store: PendenteStore, id: string, erro: string) => {
  const db = await getDB()
  const rec: any = await db.get(store, id)
  if (!rec) return
  await db.put(store, {
    ...rec,
    ultima_tentativa_em: new Date().toISOString(),
    ultimo_erro: erro,
  })
}

export const descartarPendente = async (store: PendenteStore, id: string) => {
  const db = await getDB()
  await db.delete(store, id)
}

export const contarTodosPendentes = async (): Promise<{ total: number; porStore: Record<PendenteStore, number> }> => {
  const db = await getDB()
  const porStore = {} as Record<PendenteStore, number>
  let total = 0
  for (const s of ALL_STORES) {
    let n = 0
    try {
      n = s === 'rdos_pendentes'
        ? await db.countFromIndex(s, 'status', 'pendente')
        : await db.countFromIndex(s, 'status', 'pendente')
    } catch { n = 0 }
    porStore[s] = n
    total += n
  }
  return { total, porStore }
}

// ───────────────────────────────────────────────
// API legada de RDO (Etapa 3) — mantida intacta
// ───────────────────────────────────────────────
export const salvarRDOOffline = async (rdo: RDOPendentePayload): Promise<RDOPendenteRecord> => {
  const db = await getDB()
  const id = newId('rdo_offline')
  const record: RDOPendenteRecord = {
    ...rdo,
    id,
    status: 'pendente',
    criado_offline_em: new Date().toISOString(),
    ultima_tentativa_em: null,
    ultimo_erro: null,
  }
  await db.put('rdos_pendentes', record)
  return record
}

export const listarRDOsPendentes = async (): Promise<RDOPendenteRecord[]> => {
  const db = await getDB()
  return db.getAllFromIndex('rdos_pendentes', 'status', 'pendente') as Promise<RDOPendenteRecord[]>
}

export const listarRDOsPendentesPorObra = async (obraId: string): Promise<RDOPendenteRecord[]> => {
  const all = await listarRDOsPendentes()
  return all.filter(r => r.obra_id === obraId)
}

export const contarRDOsPendentes = async (): Promise<number> => {
  const db = await getDB()
  return db.countFromIndex('rdos_pendentes', 'status', 'pendente')
}

export const marcarRDOErro = async (id: string, erro: string) => {
  const db = await getDB()
  const rec = (await db.get('rdos_pendentes', id)) as RDOPendenteRecord | undefined
  if (!rec) return
  await db.put('rdos_pendentes', {
    ...rec,
    ultima_tentativa_em: new Date().toISOString(),
    ultimo_erro: erro,
  })
}

export const deletarRDOSincronizado = async (id: string) => {
  const db = await getDB()
  await db.delete('rdos_pendentes', id)
}
